import { useRef, useState } from "react";
import { GroupBase, MultiValueProps, OnChangeValue } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { Popover, Whisper } from "rsuite";
import { useDataset } from "../DataSet/DatasetProvider";
import { OptionType, StandardFilterProps } from "../types";

const LimitedMultiValue = (
  props: MultiValueProps<OptionType, true, GroupBase<OptionType>>,
) => {
  const { index, getValue, data } = props;
  const total = getValue().length;

  if (index > 0) {
    return null;
  }

  return (
    <>
      <span className="selected-value-text">{data.label}</span>
      <span className="rs-picker-value-count">{total}</span>
    </>
  );
};

interface IDynamicAsyncDropDownProps extends StandardFilterProps {}

const DynamicAsyncDropDown = (props: IDynamicAsyncDropDownProps) => {
  const {
    asyncFilterLoadOptions,
    getFilterValue,
    setFilterValue,
    getFiltersStateParam,
    setFiltersStateParam,
    filtersVersion,
  } = useDataset();

  const inputValue =
    (getFiltersStateParam(props.filterDef.code) as string) ?? "";

  const containerRef = useRef<HTMLDivElement>(null);
  const [menuPlacement, setMenuPlacement] = useState<"bottom" | "top">(
    "bottom",
  );

  const customStyles = {
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999,
    }),
    valueContainer: (base: any) => ({
      ...base,
      flexWrap: "nowrap" as const,
    }),
  };

  const loadOptions: LoadOptions<
    OptionType,
    GroupBase<OptionType>,
    { page: number }
  > = asyncFilterLoadOptions[props.filterDef.code];

  const value = (getFilterValue(props.filterDef.code) ?? []) as OptionType[];

  const speaker = (
    <Popover className={value.length === 0 ? "hidden" : ""}>
      <p className="font-medium">{props.filterDef.name}</p>
      {value.map((v, i) => (
        <li key={i} className="ml-3">
          {v.label}
        </li>
      ))}
    </Popover>
  );

  return (
    <Whisper
      placement={props.filterDef.pinned ? "auto" : "right"}
      trigger={["focus", "hover"]}
      speaker={speaker}
      preventOverflow
      delayOpen={100}
    >
      <div ref={containerRef}>
        <AsyncPaginate
          key={filtersVersion}
          placeholder={props.placeholder}
          isMulti={true}
          styles={customStyles}
          components={{ MultiValue: LimitedMultiValue }}
          value={value}
          loadOptions={loadOptions}
          additional={{ page: 1 }}
          debounceTimeout={500}
          className={`DynamicAsyncDropDown ${value.length > 0 ? "filled" : ""}`}
          autoFocus={false}
          isSearchable={true}
          isClearable={true}
          inputValue={inputValue}
          onChange={(value: OnChangeValue<OptionType, true>, actionMeta) => {
            switch (actionMeta.action) {
              case "clear":
                setFiltersStateParam(props.filterDef.code, "");
                setFilterValue(props.filterDef.code, []);
                break;
              case "select-option":
                setFiltersStateParam(props.filterDef.code, "");
                setFilterValue(
                  props.filterDef.code,
                  (value as OptionType[]).map((v) => v),
                );
                break;
              default:
                setFilterValue(
                  props.filterDef.code,
                  (value as OptionType[]).map((v) => v),
                );
                break;
            }
          }}
          onInputChange={(value, actionMeta) => {
            if (actionMeta.action === "input-change") {
              setFiltersStateParam(props.filterDef.code, value);
            }
            return value;
          }}
          onMenuOpen={() => {
            const rect = containerRef.current?.getBoundingClientRect();
            if (rect) {
              const spaceBelow = window.innerHeight - rect.bottom;
              setMenuPlacement(spaceBelow < 300 ? "top" : "bottom");
            }
          }}
          menuPosition="fixed"
          menuPortalTarget={document.body}
          menuPlacement={menuPlacement}
          menuShouldScrollIntoView={false}
        />
      </div>
    </Whisper>
  );
};

export default DynamicAsyncDropDown;
